/**
 * Team Route.
 *
 * Navigation URLs are generated just in time by the server. Protected routes
 * receive no external URL, so client styling cannot reveal restricted data.
 */

import { useEffect, useState, type DragEvent } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapIcon,
} from '@heroicons/react/24/outline';

export interface RouteSegmentView {
  idx: number;
  firstSeq: number;
  lastSeq: number;
  url: string | null;
  appleUrl?: string | null;
}

export interface RouteStopView {
  id: string;
  sequence: number;
  addressLine: string;
  cityLine: string;
  etaLabel: string;
  durationLabel?: string;
  recordCount: number;
  personNames?: string[];
  status: 'pending' | 'completed' | 'skipped';
  navigateUrl: string | null;
  appleNavigateUrl?: string | null;
}

export interface TeamRouteData {
  teamId: string;
  teamName?: string;
  tripId?: string;
  routePlanId?: string;
  trips?: Array<{
    id: string;
    label: string;
    teamCount: number;
    firstTeamId: string;
  }>;
  teams: Array<{ id: string; name: string; routePlanId?: string; stopCount?: number }>;
  dateLabel: string;
  stopCount: number;
  visitCount: number;
  roadMiles: number;
  windowLabel: string;
  estimated: boolean;
  classification: 'LOGISTICS' | 'PROTECTED';
  navigationBlockedMessage: string | null;
  segments: RouteSegmentView[];
  stops: RouteStopView[];
  fieldModeStarted: boolean;
  canReorder?: boolean;
}

function countLabel(count: number, singular: string) {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

export function TeamRouteScreen({
  data, canReorder = false, onTeamChange, onTripChange, onReorder, onMoveStop,
  onMoveStops,
  onStartField, onShare, onAddToCalendar,
}: {
  data: TeamRouteData | null;
  canReorder?: boolean;
  onTeamChange: (teamId: string) => void;
  onTripChange?: (tripId: string, firstTeamId: string) => void;
  onReorder?: (routePlanId: string, stopIds: string[]) => Promise<TeamRouteData>;
  onMoveStop?: (routePlanId: string, stopId: string,
    targetRoutePlanId: string) => Promise<TeamRouteData>;
  onMoveStops?: (routePlanId: string, stopIds: string[],
    targetRoutePlanId: string) => Promise<TeamRouteData>;
  onStartField: () => void;
  onShare: () => void;
  onAddToCalendar: () => void;
}) {
  const [orderedStops, setOrderedStops] = useState<RouteStopView[]>([]);
  const [draggedStopId, setDraggedStopId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [moveTargets, setMoveTargets] = useState<Record<string, string>>({});
  const [selectedStops, setSelectedStops] = useState<string[]>([]);
  const [batchTarget, setBatchTarget] = useState('');

  useEffect(() => {
    setOrderedStops(data?.stops ?? []);
    setDraggedStopId(null);
    setDropTargetId(null);
    setSaveState('idle');
    setSaveMessage('');
    setSelectedStops([]);
    setBatchTarget('');
  }, [data?.routePlanId]);

  if (!data) return <p className="pt-8 text-base text-neutral-700">Loading route...</p>;

  const blocked = data.classification === 'PROTECTED';
  const selectedTeam = data.teams.find((team) => team.id === data.teamId);
  const baseName = data.teamName ?? selectedTeam?.name ?? 'Selected';
  const teamTitle = /\bteam$/i.test(baseName) ? baseName : `${baseName} Team`;
  const mayReorder = canReorder && Boolean(onReorder) && Boolean(data.routePlanId)
    && orderedStops.filter((stop) => stop.status === 'pending').length > 1;
  const reorderEnabled = mayReorder && saveState !== 'saving';

  const numbered = (stops: RouteStopView[]) => stops.map((stop, index) => ({
    ...stop,
    sequence: index + 1,
  }));

  const saveOrder = async (nextStops: RouteStopView[]) => {
    if (!mayReorder || !onReorder || !data.routePlanId) return;
    const previousStops = orderedStops;
    const nextNumbered = numbered(nextStops);
    setOrderedStops(nextNumbered);
    setSaveState('saving');
    setSaveMessage('Saving new route order...');
    try {
      const saved = await onReorder(data.routePlanId, nextNumbered.map((stop) => stop.id));
      setOrderedStops(saved.stops);
      setSaveState('saved');
      setSaveMessage('Route order saved.');
    } catch {
      setOrderedStops(previousStops);
      setSaveState('error');
      setSaveMessage('The order was not saved. Reload the route and try again.');
    }
  };

  const moveStop = (sourceId: string, targetIndex: number) => {
    const sourceIndex = orderedStops.findIndex((stop) => stop.id === sourceId);
    if (sourceIndex < 0 || sourceIndex === targetIndex) return;
    if (orderedStops[sourceIndex].status !== 'pending'
      || orderedStops[targetIndex]?.status !== 'pending') return;
    const next = [...orderedStops];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    void saveOrder(next);
  };

  const moveToTeam = async (stopId: string) => {
    const targetRoutePlanId = moveTargets[stopId];
    if (!data.routePlanId || !targetRoutePlanId || !onMoveStop) return;
    setSaveState('saving');
    setSaveMessage('Moving stop and recalculating both routes...');
    try {
      const updated = await onMoveStop(data.routePlanId, stopId, targetRoutePlanId);
      setOrderedStops(updated.stops);
      setSaveState('saved');
      setSaveMessage('Stop moved. Both routes were recalculated.');
    } catch {
      setSaveState('error');
      setSaveMessage('The move was not saved. Reload the routes and try again.');
    }
  };

  const moveSelected = async () => {
    if (!data.routePlanId || !batchTarget || !onMoveStops || selectedStops.length < 2) return;
    setSaveState('saving');
    setSaveMessage(`Moving ${selectedStops.length} stops and recalculating both routes...`);
    try {
      const updated = await onMoveStops(data.routePlanId, selectedStops, batchTarget);
      setOrderedStops(updated.stops);
      setSelectedStops([]);
      setBatchTarget('');
      setSaveState('saved');
      setSaveMessage('Selected stops moved. Both routes were recalculated.');
    } catch {
      setSaveState('error');
      setSaveMessage('The group move was not saved. Reload the routes and try again.');
    }
  };

  const beginDrag = (event: DragEvent<HTMLButtonElement>, stopId: string) => {
    if (!reorderEnabled) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', stopId);
    setDraggedStopId(stopId);
  };

  const dropStop = (event: DragEvent<HTMLLIElement>, targetIndex: number) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData('text/plain') || draggedStopId;
    setDraggedStopId(null);
    setDropTargetId(null);
    if (sourceId) moveStop(sourceId, targetIndex);
  };

  return (
    <div className="pt-4 desktop:pt-6 min-w-0">
      {!!data.trips?.length && (
        <nav className="flex gap-2 overflow-x-auto pb-2 mb-2" aria-label="Trip dates">
          {data.trips.map((trip) => (
            <button
              key={trip.id}
              type="button"
              className={`seg shrink-0 ${trip.id === data.tripId ? 'seg-active' : ''}`}
              aria-pressed={trip.id === data.tripId}
              onClick={() => onTripChange?.(trip.id, trip.firstTeamId)}
            >
              {trip.label} · {countLabel(trip.teamCount, 'team')}
            </button>
          ))}
        </nav>
      )}

      <nav className="flex gap-2 overflow-x-auto pb-2 mb-3" aria-label="Teams on this trip">
        {data.teams.map((team) => (
          <button
            key={team.id}
            type="button"
            className={`seg shrink-0 ${team.id === data.teamId ? 'seg-active' : ''}`}
            aria-pressed={team.id === data.teamId}
            onClick={() => onTeamChange(team.id)}
          >
            {team.name}{team.stopCount == null ? '' : ` · ${team.stopCount}`}
          </button>
        ))}
      </nav>

      <header className="mb-4">
        <h1 className="mb-1">{teamTitle}</h1>
        <p className="text-sm desktop:text-base text-neutral-700 leading-relaxed">
          {data.dateLabel} · {countLabel(data.stopCount, 'stop')} ·{' '}
          {countLabel(data.visitCount, 'visit')} · {data.roadMiles} mi{' '}
          {data.estimated ? 'estimated' : 'by road'} · {data.windowLabel}
        </p>
        {data.estimated && (
          <p className="mt-1 text-sm text-neutral-700">
            Distances are estimated. The road network was unavailable.
          </p>
        )}
        {!data.estimated && data.classification === 'LOGISTICS' && (
          <p className="mt-1 text-sm text-neutral-700">
            Routing: openrouteservice, OpenStreetMap contributors.
          </p>
        )}
      </header>

      <div className="grid grid-cols-1 min-[520px]:grid-cols-3 gap-2 mb-6">
        <button className="btn btn-primary w-full" onClick={onStartField}>
          {data.fieldModeStarted ? 'Continue Field Mode' : 'Start Field Mode'}
        </button>
        <button className="btn btn-secondary w-full" onClick={onShare}>Share Trip</button>
        <button className="btn btn-secondary w-full" onClick={onAddToCalendar}>
          Add to Calendar
        </button>
      </div>

      <div className="desktop:grid desktop:grid-cols-[minmax(280px,0.85fr)_minmax(440px,1.15fr)] desktop:gap-8 desktop:items-start">
        <section className="mb-6 desktop:mb-0 desktop:sticky desktop:top-[84px]">
          <h6 className="text-accent-700 mb-2">Open route in Google Maps</h6>

          {blocked ? (
            <p className="p-3 bg-neutral-200 border border-neutral-400 rounded-lg text-base">
              {data.navigationBlockedMessage ??
                'External navigation unavailable for this protected trip.'}
            </p>
          ) : (
            <ul className="flex flex-col gap-2 desktop:max-h-[58vh] desktop:overflow-y-auto desktop:pr-1">
              {data.segments.map((segment, index) => {
                const coveredStops = segment.lastSeq - segment.firstSeq + 1;
                return (
                  <li key={segment.idx} className="bg-surface border border-neutral-300 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <MapIcon className="w-5 h-5 text-accent-700 shrink-0" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-semibold text-base">
                          Segment {index + 1} of {data.segments.length}
                        </p>
                        <p className="text-sm text-neutral-700">
                          Stops {segment.firstSeq} to {segment.lastSeq} ·{' '}
                          {countLabel(coveredStops, 'stop')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {segment.url && (
                          <a
                            href={segment.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="btn btn-primary"
                          >
                            Open in Google Maps
                            <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden />
                          </a>
                        )}
                        {segment.appleUrl && (
                          <a
                            href={segment.appleUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="btn btn-ghost"
                          >
                            Open in Apple Maps
                          </a>
                        )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {!blocked && (
            <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
              Each segment begins where the previous one ended. Navigation links contain
              coordinates only. Apple Maps opens the segment's first and last stop.
            </p>
          )}
        </section>

        <section className="min-w-0">
          <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h6 className="text-accent-700">Stops</h6>
              {canReorder && orderedStops.filter((stop) => stop.status === 'pending').length > 1 && (
                <p className="mt-1 text-sm text-neutral-700">
                  Drag a handle, or use the arrow buttons, to reorder pending stops.
                </p>
              )}
              {canReorder && data.fieldModeStarted && (
                <p className="mt-1 text-sm text-neutral-700">
                  Completed stops stay fixed. Remaining stops can be reordered.
                </p>
              )}
            </div>
            {canReorder && saveMessage && (
              <p
                className={`text-sm ${saveState === 'error' ? 'text-alert-700' : 'text-neutral-700'}`}
                role="status"
                aria-live="polite"
              >
                {saveMessage}
              </p>
            )}
          </div>
          {onMoveStops && !data.fieldModeStarted && data.teams.some((team) =>
            team.id !== data.teamId && team.routePlanId) && (
            <div className="mb-3 flex flex-wrap items-end gap-2">
              <label className="field"><span>Move selected stops to</span>
                <select className="input" value={batchTarget}
                  onChange={(event) => setBatchTarget(event.target.value)}>
                  <option value="">Choose a team</option>
                  {data.teams.filter((team) => team.id !== data.teamId && team.routePlanId)
                    .map((team) => <option key={team.id} value={team.routePlanId}>
                      {team.name}
                    </option>)}
                </select>
              </label>
              <button className="btn btn-secondary" type="button"
                disabled={selectedStops.length < 2 || !batchTarget || saveState === 'saving'}
                onClick={() => void moveSelected()}>
                Move {selectedStops.length} selected stops
              </button>
            </div>
          )}
          <ol className="desktop:max-h-[68vh] desktop:overflow-y-auto desktop:pr-1">
            {orderedStops.map((stop, index) => {
              const stopName = stop.personNames?.filter(Boolean).join(', ')
                || countLabel(stop.recordCount, 'scheduled visit');
              return (
              <li
                key={stop.id}
                className={[
                  'border-b border-neutral-300 py-3 flex items-start gap-2 min-w-0',
                  dropTargetId === stop.id ? 'bg-accent-100' : '',
                ].join(' ')}
                onDragOver={(event) => {
                  if (!reorderEnabled || stop.status !== 'pending') return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDropTargetId(stop.id);
                }}
                onDragLeave={() => setDropTargetId((current) =>
                  current === stop.id ? null : current)}
                onDrop={(event) => dropStop(event, index)}
              >
                <span
                  className={[
                    'w-8 h-8 rounded-full shrink-0 flex items-center justify-center',
                    'font-heading text-sm tabular-nums',
                    stop.status === 'skipped'
                      ? 'bg-neutral-300 text-neutral-700'
                      : 'bg-accent text-white',
                  ].join(' ')}
                >
                  {stop.sequence}
                </span>

                {onMoveStops && !data.fieldModeStarted && <input
                  type="checkbox" className="mt-2"
                  aria-label={`Select ${stopName} for a group move`}
                  checked={selectedStops.includes(stop.id)}
                  onChange={(event) => setSelectedStops((current) => event.target.checked
                    ? [...current, stop.id] : current.filter((id) => id !== stop.id))}
                />}

                {canReorder && (
                  <button
                    type="button"
                    draggable={reorderEnabled && stop.status === 'pending'}
                    disabled={!reorderEnabled || stop.status !== 'pending'}
                    onDragStart={(event) => beginDrag(event, stop.id)}
                    onDragEnd={() => {
                      setDraggedStopId(null);
                      setDropTargetId(null);
                    }}
                    className="inline-flex h-touch w-touch shrink-0 items-center justify-center rounded border border-neutral-300 bg-surface text-neutral-700 disabled:cursor-not-allowed disabled:opacity-40 enabled:cursor-grab enabled:active:cursor-grabbing"
                    aria-label={`Drag ${stopName} to a new position`}
                    title="Drag to reorder"
                  >
                    <Bars3Icon className="h-5 w-5" aria-hidden />
                  </button>
                )}

                <div className="flex-1 min-w-0">
                  <p className="break-words font-heading font-semibold text-base leading-snug">
                    {stopName}
                  </p>
                  <p className="break-words text-base leading-snug">
                    {stop.addressLine}
                  </p>
                  <p className="text-sm text-neutral-700 leading-snug">
                    {stop.etaLabel} · {stop.durationLabel ?? '20 min'} ·{' '}
                    {countLabel(stop.recordCount, 'visit')}
                  </p>
                  {stop.cityLine && (
                    <p className="text-xs text-neutral-600 truncate">{stop.cityLine}</p>
                  )}
                  {canReorder && onMoveStop && !data.fieldModeStarted
                    && data.teams.some((team) => team.id !== data.teamId && team.routePlanId) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <select className="input max-w-48" aria-label={`Destination route for ${stopName}`}
                        value={moveTargets[stop.id] ?? ''}
                        onChange={(event) => setMoveTargets((current) => ({
                          ...current, [stop.id]: event.target.value,
                        }))}>
                        <option value="">Move to team...</option>
                        {data.teams.filter((team) => team.id !== data.teamId && team.routePlanId)
                          .map((team) => <option key={team.id} value={team.routePlanId}>
                            {team.name}
                          </option>)}
                      </select>
                      <button className="btn btn-secondary" type="button"
                        disabled={!moveTargets[stop.id] || saveState === 'saving'}
                        onClick={() => void moveToTeam(stop.id)}>Move stop</button>
                    </div>
                  )}
                </div>

                {canReorder && (
                  <div className="flex shrink-0 flex-col gap-1" aria-label={`Move ${stopName}`}>
                    <button
                      type="button"
                      className="inline-flex h-touch w-touch items-center justify-center rounded border border-neutral-300 bg-surface text-neutral-800 disabled:cursor-not-allowed disabled:opacity-30"
                      disabled={!reorderEnabled || index === 0
                        || stop.status !== 'pending'
                        || orderedStops[index - 1]?.status !== 'pending'}
                      onClick={() => moveStop(stop.id, index - 1)}
                      aria-label={`Move ${stopName} up`}
                      title="Move up"
                    >
                      <ChevronUpIcon className="h-4 w-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-touch w-touch items-center justify-center rounded border border-neutral-300 bg-surface text-neutral-800 disabled:cursor-not-allowed disabled:opacity-30"
                      disabled={!reorderEnabled || index === orderedStops.length - 1
                        || stop.status !== 'pending'
                        || orderedStops[index + 1]?.status !== 'pending'}
                      onClick={() => moveStop(stop.id, index + 1)}
                      aria-label={`Move ${stopName} down`}
                      title="Move down"
                    >
                      <ChevronDownIcon className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                )}

                {!blocked && (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {stop.navigateUrl && (
                      <a
                        href={stop.navigateUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="btn btn-ghost px-2"
                      >
                        Navigate
                      </a>
                    )}
                    {stop.appleNavigateUrl && (
                      <a
                        href={stop.appleNavigateUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="btn btn-ghost px-2 text-neutral-700"
                      >
                        Apple
                      </a>
                    )}
                  </div>
                )}
              </li>
              );
            })}
          </ol>
        </section>
      </div>
    </div>
  );
}
